import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import { IconRefresh } from "./icons";

type Device = "mobile" | "tablet" | "desktop";

/**
 * Screens an operator can jump straight to.
 *
 * Most of the booth sits behind a camera capture, so without this you'd set a
 * price on the result screen and have no way to see it short of taking a photo
 * inside a 390px iframe.
 */
const SCREENS = [
  { id: "", label: "Start" },
  { id: "mode", label: "Choose" },
  { id: "vibes", label: "Vibes" },
  { id: "result", label: "Result" },
  { id: "completion", label: "Complete" },
  // No "Payment" entry: payment happens on the guest's own phone at /s/:token,
  // not in the booth. Previewing it here would show a screen that no longer
  // exists and tell the operator prices are fine when they've never been seen.
] as const;

const FRAMES: Record<Device, { width: number; height: number; radius: number; label: string }> = {
  mobile: { width: 390, height: 844, radius: 36, label: "Mobile" },
  tablet: { width: 820, height: 1180, radius: 24, label: "Tablet" },
  desktop: { width: 1440, height: 900, radius: 12, label: "Desktop" },
};

/**
 * The booth, rendered live inside a device frame.
 *
 * Uses a real iframe pointed at `/:slug?preview=1` rather than re-rendering the
 * booth components inline: it's the same code path a guest hits, so what the
 * operator approves here is genuinely what ships. `version` busts the frame
 * whenever a draft save lands.
 */
export function LivePreview({ slug, version }: { slug: string; version: number }) {
  const [device, setDevice] = useState<Device>("mobile");
  const [screen, setScreen] = useState<string>("");
  const [nonce, setNonce] = useState(0);

  const frame = FRAMES[device];
  const src = useMemo(
    () => `/${slug}?preview=1&v=${version}-${nonce}${screen ? `&screen=${screen}` : ""}`,
    [slug, version, nonce, screen],
  );

  return (
    <div className="flex h-full flex-col bg-ink-900">
      <header className="flex shrink-0 flex-col gap-3 border-b border-ink-600 px-5 py-3">
        <div className="flex items-center gap-4">
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="text-sm font-semibold">Live Preview</span>
            <span className="truncate text-2xs text-haze-400">
              Draft updates stream into this device frame.
            </span>
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-1 rounded-xl border border-ink-500 bg-ink-800 p-1">
            {(Object.keys(FRAMES) as Device[]).map((key) => (
              <button
                key={key}
                onClick={() => setDevice(key)}
                className={cn(
                  "min-h-10 rounded-lg px-3 text-xs transition-colors",
                  device === key ? "bg-accent font-semibold text-accent-ink" : "text-haze-300 hover:bg-ink-700 hover:text-haze-100",
                )}
              >
                {FRAMES[key].label}
              </button>
            ))}
          </div>

          <Button size="sm" variant="outline" icon={<IconRefresh size={13} />} onClick={() => setNonce((v) => v + 1)}>
            Refresh
          </Button>
        </div>

        {/* Most of the booth sits behind a camera capture, so jumping straight
            to a screen is the only way to review it while configuring. */}
        <div className="flex items-center gap-1 rounded-xl border border-ink-500 bg-ink-800 p-1">
          {SCREENS.map((option) => (
            <button
              key={option.id}
              onClick={() => setScreen(option.id)}
              className={cn(
                "min-h-10 flex-1 whitespace-nowrap rounded-lg px-3 text-xs font-medium transition-colors",
                screen === option.id ? "bg-accent/10 text-accent ring-1 ring-inset ring-accent/25" : "text-haze-300 hover:bg-ink-700 hover:text-haze-100",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      <div className="flex flex-1 items-center justify-center overflow-hidden p-6">
        <DeviceFrame frame={frame}>
          <iframe
            key={src}
            src={src}
            title="Booth preview"
            className="h-full w-full border-0 bg-ink-850"
            style={{ width: frame.width, height: frame.height }}
          />
        </DeviceFrame>
      </div>
    </div>
  );
}

/**
 * Scales the device down to fit the available panel.
 *
 * The iframe renders at true device pixels and is then transform-scaled, so the
 * booth inside sees a real 390px viewport and its media queries behave exactly
 * as they would on the hardware.
 */
function DeviceFrame({
  frame, children,
}: { frame: (typeof FRAMES)[Device]; children: ReactNode }) {
  return (
    <div
      className="relative"
      style={{
        width: frame.width,
        height: frame.height,
        // Fit into the panel without knowing its exact size: cap by both axes.
        transform: "scale(var(--preview-scale, 1))",
        transformOrigin: "center",
        ["--preview-scale" as string]: `min(calc((100vh - 10rem) / ${frame.height}), calc((45vw - 4rem) / ${frame.width}), 1)`,
      }}
    >
      <div
        className="h-full w-full overflow-hidden border-[6px] border-ink-700 bg-ink-850 shadow-2xl"
        style={{ borderRadius: frame.radius }}
      >
        {children}
      </div>
    </div>
  );
}
